import React from 'react';
import { t } from '@lingui/macro';

import { CarouselLeftSmall, CarouselRightSmall } from '@rover/icons';
import { Flex } from '@rover/kibble/core';
import Button from '@rover/kibble/core/Button';

import type { OnPaginateAction } from '../../utils/onPaginateEvent.types';
import { CarouselScrollSource } from '../../utils/onPaginateEvent.types';

export enum PaginateDirection {
  NEXT = 1,
  PREVIOUS = -1,
}

type Props = React.ComponentProps<typeof Flex> & {
  currentPage: number;
  totalPages: number;
  handlePageChange: (direction: PaginateDirection) => void;
  onClickTrackEvent?: OnPaginateAction;
};

/**
 * Note: To make development easier and quickly fix an issue with pagination on the v2 carousels, I duplicated the Stepper to preserve its functionality for the v1 SnappingCarousel so that I could implement a proper fix for the v2 carousel pagination issue in DEV-130216
 * DEV-128063 - When we remove the SnappingCarouselV1, we can remove this file as well
 * @deprecated
 */
export const Stepper: React.FC<Props> = ({
  handlePageChange,
  currentPage,
  totalPages,
  onClickTrackEvent = () => {},
  ...flexProps
}) => {
  return (
    <Flex
      minWidth="15x"
      gap="5x"
      flex="none"
      alignItems="center"
      justifyContent={['center', 'right']}
      {...flexProps}
    >
      <Button
        data-qa-id="paginate-previous"
        icon={CarouselLeftSmall}
        disabled={currentPage === 1}
        circular
        aria-label={t`paginate previous`}
        onClick={() => {
          onClickTrackEvent({
            pageStart: currentPage,
            pageEnd: currentPage - 1,
            scrollSource: CarouselScrollSource.BUTTON,
          });
          handlePageChange(PaginateDirection.PREVIOUS);
        }}
      />
      {`${currentPage > totalPages ? totalPages : currentPage} / ${totalPages}`}
      <Button
        data-qa-id="paginate-next"
        icon={CarouselRightSmall}
        disabled={currentPage >= totalPages}
        circular
        aria-label={t`paginate next`}
        onClick={() => {
          onClickTrackEvent({
            pageStart: currentPage,
            pageEnd: currentPage + 1,
            scrollSource: CarouselScrollSource.BUTTON,
          });
          handlePageChange(PaginateDirection.NEXT);
        }}
      />
    </Flex>
  );
};
